import type { DeliveryPlatform, PaymentMethod } from './types';
import type { DailyRevenueInput } from './validation';

/**
 * Importador universal de ventas del TPV (CSV / Excel).
 *
 * Decision de la reunion con Alex: antes de integrarnos con TPV concretos,
 * un importador de archivo con mapeo de columnas que trague lo que exporte
 * cualquier caja. Todo lo que se puede probar sin navegador vive aqui:
 * deteccion de separador, numeros en formato espanol o ingles, fechas
 * ambiguas (DD/MM vs MM/DD), adivinado de columnas por cabecera, agregado
 * por dia y emparejado de platos por nombre.
 *
 * La lectura del archivo (FileReader, SheetJS) es cosa de la web.
 */

/* ------------------------------------------------------------------ */
/* Parseo de CSV                                                       */
/* ------------------------------------------------------------------ */

const DELIMITERS = [';', ',', '\t'] as const;
export type Delimiter = (typeof DELIMITERS)[number];

/**
 * Elige el separador que produce mas columnas de forma consistente en las
 * primeras lineas. Los TPV espanoles exportan casi siempre con `;` (porque
 * la coma es el decimal); los americanos con `,`.
 */
export function detectDelimiter(text: string): Delimiter {
  const lines = text.split(/\r?\n/, 10).filter((line) => line.trim() !== '');
  let best: Delimiter = ';';
  let bestScore = 0;
  for (const delimiter of DELIMITERS) {
    const counts = lines.map((line) => splitCsvLine(line, delimiter).length);
    if (counts.length === 0) continue;
    const min = Math.min(...counts);
    const max = Math.max(...counts);
    // Puntua: muchas columnas y numero de columnas estable entre lineas.
    const score = min > 1 && min === max ? min * 2 : min;
    if (score > bestScore) {
      bestScore = score;
      best = delimiter;
    }
  }
  return best;
}

/** Trocea UNA linea respetando comillas dobles ("a;b" no separa). */
function splitCsvLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
}

/**
 * CSV completo → matriz de celdas. Detecta el separador si no se fuerza,
 * respeta comillas (incluidos saltos de linea dentro de comillas) y
 * descarta filas totalmente vacias.
 */
export function parseCsv(text: string, delimiter?: Delimiter): string[][] {
  const clean = text.replace(/^﻿/, ''); // BOM de Excel
  const sep = delimiter ?? detectDelimiter(clean);

  // Re-une lineas partidas dentro de comillas antes de trocear.
  const rawLines = clean.split(/\r?\n/);
  const lines: string[] = [];
  let pending = '';
  for (const raw of rawLines) {
    const candidate = pending === '' ? raw : `${pending}\n${raw}`;
    const quotes = (candidate.match(/"/g) ?? []).length;
    if (quotes % 2 === 1) {
      pending = candidate;
    } else {
      lines.push(candidate);
      pending = '';
    }
  }
  if (pending !== '') lines.push(pending);

  return lines
    .map((line) => splitCsvLine(line, sep))
    .filter((cells) => cells.some((cell) => cell !== ''));
}

/* ------------------------------------------------------------------ */
/* Numeros y fechas                                                    */
/* ------------------------------------------------------------------ */

/**
 * Numero en formato espanol ("1.234,56"), ingles ("1,234.56") o plano.
 * Tolera simbolo de moneda y espacios. Devuelve undefined si no es numero.
 */
export function parseImportNumber(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  let s = raw.replace(/[€$\s ]/g, '').replace(/[A-Za-z]+$/, '');
  if (s === '' || s === '-') return undefined;
  const negative = /^\(.*\)$/.test(s);
  if (negative) s = s.slice(1, -1);

  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  if (hasDot && hasComma) {
    // El separador que aparece MAS A LA DERECHA es el decimal.
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else if (hasComma) {
    // "1,234,567" es agrupacion inglesa; "12,5" es decimal espanol.
    s = /^\d{1,3}(,\d{3}){2,}$/.test(s) ? s.replace(/,/g, '') : s.replace(',', '.');
  } else if (hasDot) {
    // "1.234.567" es agrupacion espanola; "12.5" es decimal.
    if (/^\d{1,3}(\.\d{3}){2,}$/.test(s)) s = s.replace(/\./g, '');
  }

  const value = Number(s);
  if (!Number.isFinite(value)) return undefined;
  return negative ? -value : value;
}

const ISO_DATE = /^(\d{4})-(\d{1,2})-(\d{1,2})/;
const SLASH_DATE = /^(\d{1,3})[/.\-](\d{1,2})[/.\-](\d{2,4})$/;

/**
 * ¿La columna de fechas va dia-primero (DD/MM) o mes-primero (MM/DD)?
 * Se decide mirando TODA la columna: si algun primer numero pasa de 12,
 * es dia-primero; si algun segundo numero pasa de 12, es mes-primero.
 * En empate gana dia-primero, que es lo normal en el mercado de Alex.
 */
export function detectDayFirst(values: Array<string | undefined>): boolean {
  for (const value of values) {
    const m = value?.trim().match(SLASH_DATE);
    if (!m) continue;
    const first = Number(m[1]);
    const second = Number(m[2]);
    if (first > 12 && first <= 31) return true;
    if (second > 12) return false;
  }
  return true;
}

/** Fecha de una celda → YYYY-MM-DD, o undefined si no se entiende. */
export function parseImportDate(raw: string | undefined, dayFirst: boolean): string | undefined {
  if (!raw) return undefined;
  const s = raw.trim();

  const iso = s.match(ISO_DATE);
  if (iso) return buildDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const m = s.match(SLASH_DATE);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    let year = Number(m[3]);
    if (m[3].length === 2) year += year >= 70 ? 1900 : 2000;
    // Si un componente no puede ser mes, decide solo; si no, manda dayFirst.
    const [day, month] = a > 12 ? [a, b] : b > 12 ? [b, a] : dayFirst ? [a, b] : [b, a];
    return buildDate(year, month, day);
  }
  return undefined;
}

function buildDate(year: number, month: number, day: number): string | undefined {
  if (year < 2000 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
    return undefined;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return undefined;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/* ------------------------------------------------------------------ */
/* Destinos de columna y adivinado por cabecera                        */
/* ------------------------------------------------------------------ */

export type ImportTarget =
  | { kind: 'ignore' }
  | { kind: 'date' }
  /** Total del dia sin desglose por metodo → se guarda en "otros metodos". */
  | { kind: 'total' }
  | { kind: 'inHouse'; method: PaymentMethod }
  | { kind: 'delivery'; platformId: string }
  | { kind: 'stat'; name: 'customers' | 'tables' | 'deliveryOrders' | 'courtesies' | 'discounts' }
  /**
   * Ticket medio del día. Muchos TPV dan el ticket medio ya calculado y NO los
   * comensales (petición de Alex). No se guarda como tal: se usa para DERIVAR
   * los comensales (comensales = ventas del día / ticket medio).
   */
  | { kind: 'avgTicket' }
  | { kind: 'dish' }
  /** Codigo del plato (el de la ficha de la receta): mas fiable que el nombre. */
  | { kind: 'code' }
  | { kind: 'units' };

/** Clave estable para pintar selects y comparar destinos. */
export function targetKey(target: ImportTarget): string {
  switch (target.kind) {
    case 'inHouse':
      return `inHouse:${target.method}`;
    case 'delivery':
      return `delivery:${target.platformId}`;
    case 'stat':
      return `stat:${target.name}`;
    default:
      return target.kind;
  }
}

/** Sin tildes, minusculas, sin signos: para comparar cabeceras y platos. */
export function normalizeText(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface HeaderRule {
  test: (header: string) => boolean;
  target: ImportTarget;
}

function wordRule(words: string[], target: ImportTarget): HeaderRule {
  return {
    target,
    test: (header) => {
      const tokens = header.split(' ');
      return words.some((word) => tokens.includes(word) || header.includes(word));
    },
  };
}

/**
 * Reglas en orden de prioridad: la primera que casa gana. Las especificas
 * (efectivo, credito) van antes que las genericas (total, importe).
 */
function revenueRules(platforms: DeliveryPlatform[]): HeaderRule[] {
  const platformRules = platforms.map<HeaderRule>((platform) => ({
    target: { kind: 'delivery', platformId: platform.platformId },
    test: (header) => {
      const name = normalizeText(platform.name);
      return name !== '' && header.includes(name);
    },
  }));
  return [
    wordRule(['fecha', 'date', 'dia', 'day'], { kind: 'date' }),
    ...platformRules,
    wordRule(['efectivo', 'cash', 'contado', 'metalico'], {
      kind: 'inHouse',
      method: 'efectivo',
    }),
    // Débito y crédito se unificaron en "tarjeta": todas las variantes de
    // pago con tarjeta caen aquí (débito, crédito, Visa, Mastercard, datáfono…).
    wordRule(
      ['tarjeta', 'card', 'datafono', 'tpv', 'debito', 'credito', 'visa', 'mastercard'],
      { kind: 'inHouse', method: 'tarjeta' },
    ),
    wordRule(['bizum', 'transferencia', 'vales', 'cheque'], {
      kind: 'inHouse',
      method: 'otros',
    }),
    // Antes que el importe de domicilio: «Pedidos a domicilio» contiene
    // «domicilio» y, si no, se leeria como dinero.
    wordRule(
      ['pedidos a domicilio', 'pedidos domicilio', 'pedidos delivery', 'delivery orders',
        'ordenes a domicilio', 'ordenes domicilio', 'ordenes delivery'],
      { kind: 'stat', name: 'deliveryOrders' },
    ),
    wordRule(['delivery', 'domicilio', 'reparto'], { kind: 'delivery', platformId: '' }),
    // Ticket medio: antes que "comensales" no hace falta, pero antes que "total".
    wordRule(['ticket medio', 'ticket', 'tm', 'gasto medio', 'average ticket', 'ticket promedio'], {
      kind: 'avgTicket',
    }),
    wordRule(['comensal', 'cliente', 'pax', 'cubierto', 'customer', 'guest'], {
      kind: 'stat',
      name: 'customers',
    }),
    wordRule(['mesa', 'table'], { kind: 'stat', name: 'tables' }),
    wordRule(['cortesia', 'invitacion', 'comp'], { kind: 'stat', name: 'courtesies' }),
    wordRule(['descuento', 'discount', 'dto'], { kind: 'stat', name: 'discounts' }),
    wordRule(['total', 'venta', 'importe', 'ingreso', 'facturacion', 'neto', 'amount'], {
      kind: 'total',
    }),
  ];
}

/** Cabeceras de la plantilla de unidades vendidas (mejora 64 de Alex, 6 sep 2026). */
export const UNIDADES_TEMPLATE_HEADERS = ['Código del plato', 'Nombre del plato', 'Unidades vendidas'];

const UNITS_RULES: HeaderRule[] = [
  wordRule(['unidad', 'unidades', 'uds', 'ud', 'cantidad', 'cant', 'qty', 'quantity', 'vendido'], {
    kind: 'units',
  }),
  // Antes que «plato»: la cabecera «Código del plato» de la plantilla es codigo.
  wordRule(['codigo', 'code', 'sku', 'referencia', 'ref', 'plu'], { kind: 'code' }),
  wordRule(
    ['plato', 'producto', 'articulo', 'item', 'nombre', 'descripcion', 'concepto', 'name'],
    { kind: 'dish' },
  ),
];

function guessWithRules(headers: string[], rules: HeaderRule[]): ImportTarget[] {
  const taken = new Set<string>();
  return headers.map((header) => {
    const normalized = normalizeText(header);
    if (normalized === '') return { kind: 'ignore' } as ImportTarget;
    for (const rule of rules) {
      if (!rule.test(normalized)) continue;
      const key = targetKey(rule.target);
      // Un destino de valor unico (fecha, efectivo…) no se asigna dos veces.
      if (key !== 'ignore' && taken.has(key)) continue;
      taken.add(key);
      return rule.target;
    }
    return { kind: 'ignore' } as ImportTarget;
  });
}

/** Adivina el destino de cada columna para el modo "ventas diarias". */
export function guessRevenueColumns(
  headers: string[],
  platforms: DeliveryPlatform[],
): ImportTarget[] {
  const guesses = guessWithRules(headers, revenueRules(platforms));
  // Si hay desglose por metodo, la columna "total" sobra (seria doble cuenta).
  const hasBreakdown = guesses.some((g) => g.kind === 'inHouse' || g.kind === 'delivery');
  return guesses.map((guess) =>
    guess.kind === 'total' && hasBreakdown ? { kind: 'ignore' } : guess,
  );
}

/** Adivina el destino de cada columna para el modo "unidades por plato". */
export function guessUnitsColumns(headers: string[]): ImportTarget[] {
  return guessWithRules(headers, UNITS_RULES);
}

/**
 * ¿La primera fila es cabecera? Lo es si alguna celda casa con una regla
 * conocida y ninguna parece un numero o una fecha.
 */
export function looksLikeHeader(row: string[]): boolean {
  const hasData = row.some(
    (cell) => parseImportNumber(cell) !== undefined || parseImportDate(cell, true) !== undefined,
  );
  return !hasData;
}

/* ------------------------------------------------------------------ */
/* Modo 1: ventas diarias → DailyRevenueInput[]                        */
/* ------------------------------------------------------------------ */

export interface RevenueImportResult {
  days: DailyRevenueInput[];
  /** Filas descartadas por fecha ilegible o sin ningun importe. */
  skippedRows: number;
  /**
   * Cuales y por que, para que el usuario pueda ir a la fila (Kilo, oct 2026:
   * «faltan filas» sin decir cuales no se puede corregir). `row` cuenta desde
   * 0 sobre las filas de datos, sin la cabecera.
   */
  skippedDetail: Array<{ row: number; reason: string }>;
  /** Avisos legibles para ensenar en la previsualizacion. */
  warnings: string[];
}

/**
 * Que trae el fichero del TPV en las columnas de dinero.
 *
 * Alexia trabaja SIN IVA en todo el analisis, y hasta ahora el importador daba
 * por hecho que lo que marcaba el usuario ya venia neto. Segun el TPV eso es
 * verdad ("Ventas netas") o falso ("Total del dia"), y colar ventas con IVA
 * como si fueran sin IVA infla la facturacion, el ticket medio y todos los
 * margenes de golpe: un error dificilisimo de ver despues. Por eso se
 * pregunta en vez de suponerlo (peticion de Alex, 1 sep 2026).
 */
export interface RevenueImportOptions {
  /** true = hay que quitarles el IVA; false = ya vienen netos. */
  amountsIncludeVat: boolean;
  /** Tipo con el que se descuenta. El de los ajustes del restaurante. */
  vatPct: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Filas mapeadas → un DailyRevenueInput por fecha. Si el TPV exporta varias
 * filas del mismo dia (una por turno, por ejemplo) se suman. Importes
 * negativos se admiten (abonos) pero el dia no puede quedar negativo.
 */
export function buildRevenueImport(
  rows: string[][],
  targets: ImportTarget[],
  options?: RevenueImportOptions,
): RevenueImportResult {
  const dateIndex = targets.findIndex((t) => t.kind === 'date');
  if (dateIndex === -1) {
    return { days: [], skippedRows: rows.length, skippedDetail: [], warnings: ['Falta la columna de fecha.'] };
  }
  const dayFirst = detectDayFirst(rows.map((row) => row[dateIndex]));

  /*
   * Solo el DINERO pierde el IVA: comensales y mesas son recuentos. El ticket
   * medio si es dinero y tambien se convierte — al dividir ventas entre ticket
   * para sacar los comensales el tipo se cancela, pero dejarlo a medias haria
   * que el numero cambiara segun la respuesta, que es peor.
   */
  const factorIva =
    options?.amountsIncludeVat && options.vatPct > 0 ? 1 + options.vatPct / 100 : 1;
  const neto = (value: number): number => (factorIva === 1 ? value : round2(value / factorIva));

  const byDate = new Map<string, DailyRevenueInput>();
  // Ticket medio leído por día: no se guarda, deriva los comensales al final.
  const avgTicketByDate = new Map<string, number>();
  let skippedRows = 0;
  const skippedDetail: Array<{ row: number; reason: string }> = [];
  const warnings: string[] = [];

  for (const [rowIndex, row] of rows.entries()) {
    const date = parseImportDate(row[dateIndex], dayFirst);
    if (!date) {
      skippedRows++;
      const crudo = (row[dateIndex] ?? '').trim();
      skippedDetail.push({
        row: rowIndex,
        reason: crudo ? `fecha que no se entiende: «${crudo.slice(0, 30)}»` : 'sin fecha',
      });
      continue;
    }
    let day = byDate.get(date);
    if (!day) {
      day = {
        date,
        inHouse: { efectivo: 0, tarjeta: 0, otros: 0 },
        delivery: {},
      };
      byDate.set(date, day);
    }

    let rowHasValue = false;
    targets.forEach((target, index) => {
      if (target.kind === 'ignore' || target.kind === 'date') return;
      const value = parseImportNumber(row[index]);
      if (value === undefined) return;
      rowHasValue = true;
      if (target.kind === 'total') {
        day.inHouse!.otros = round2((day.inHouse!.otros ?? 0) + neto(value));
      } else if (target.kind === 'inHouse') {
        day.inHouse![target.method] = round2((day.inHouse![target.method] ?? 0) + neto(value));
      } else if (target.kind === 'delivery') {
        const id = target.platformId || 'delivery';
        day.delivery![id] = round2((day.delivery![id] ?? 0) + neto(value));
      } else if (target.kind === 'avgTicket') {
        // Última lectura no nula del día (normalmente hay una fila por día).
        if (value > 0) avgTicketByDate.set(date, neto(value));
      } else if (target.kind === 'stat') {
        const current = (day[target.name] as number | undefined) ?? 0;
        const esRecuento =
          target.name === 'customers' || target.name === 'tables' || target.name === 'deliveryOrders';
        const next = esRecuento
          ? Math.round(current + value)
          : round2(current + neto(value));
        (day as Record<string, unknown>)[target.name] = next;
      }
    });
    if (!rowHasValue) {
      skippedRows++;
      skippedDetail.push({ row: rowIndex, reason: 'ningún importe legible en las columnas marcadas' });
    }
  }

  // Un abono puede dejar un metodo en negativo: se avisa y se recorta a 0,
  // porque el modelo (y el Excel de Alex) no admiten ventas negativas.
  for (const day of byDate.values()) {
    const clamp = (value: number | undefined): number => Math.max(0, value ?? 0);
    const negatives =
      Object.values(day.inHouse ?? {}).some((v) => (v ?? 0) < 0) ||
      Object.values(day.delivery ?? {}).some((v) => (v ?? 0) < 0);
    if (negatives) {
      warnings.push(`El día ${day.date} quedaba negativo tras los abonos; se ha dejado en 0.`);
      for (const method of Object.keys(day.inHouse!) as PaymentMethod[]) {
        day.inHouse![method] = clamp(day.inHouse![method]);
      }
      for (const id of Object.keys(day.delivery!)) {
        day.delivery![id] = clamp(day.delivery![id]);
      }
    }
  }

  /*
   * Descuentos y cortesias: muchos TPV los exportan con signo menos ("-35").
   * La API exige importes >= 0, y antes ese "-35" hacia fallar el dia entero
   * y cortaba la importacion ahi (Kilo, oct 2026: enero y febrero no
   * terminaban). Lo que se quiere guardar es cuanto se desconto: el valor
   * absoluto. Un recuento negativo (comensales, mesas) no tiene lectura y se
   * deja en 0.
   */
  let invertidos = 0;
  for (const day of byDate.values()) {
    for (const name of ['discounts', 'courtesies'] as const) {
      const value = day[name];
      if (value != null && value < 0) {
        day[name] = round2(Math.abs(value));
        invertidos++;
      }
    }
    for (const name of ['customers', 'tables', 'deliveryOrders'] as const) {
      const value = day[name];
      if (value != null && value < 0) day[name] = 0;
    }
  }
  if (invertidos > 0) {
    warnings.push(
      `${invertidos} ${invertidos === 1 ? 'día traía' : 'días traían'} descuentos o cortesías en negativo; se han guardado como importe descontado (sin el signo menos).`,
    );
  }

  // Derivar comensales del ticket medio, cuando el TPV lo da hecho y NO da los
  // comensales (petición de Alex). Solo si el día no traía comensales propios.
  let derivados = 0;
  for (const day of byDate.values()) {
    const ticket = avgTicketByDate.get(day.date);
    if (!ticket || ticket <= 0) continue;
    if (day.customers != null && day.customers > 0) continue;
    const ventas =
      Object.values(day.inHouse ?? {}).reduce((a, b) => a + (b ?? 0), 0) +
      Object.values(day.delivery ?? {}).reduce((a, b) => a + (b ?? 0), 0);
    if (ventas <= 0) continue;
    day.customers = Math.max(1, Math.round(ventas / ticket));
    derivados++;
  }
  if (factorIva !== 1) {
    warnings.push(
      `Has indicado que los importes vienen con IVA: se les ha quitado el ${options!.vatPct} % para guardarlos netos, que es como Alexia analiza las ventas.`,
    );
  }
  if (derivados > 0) {
    warnings.push(
      `Comensales calculados a partir del ticket promedio en ${derivados} ${derivados === 1 ? 'día' : 'días'} (ventas ÷ ticket promedio).`,
    );
  }

  const days = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  return { days, skippedRows, skippedDetail, warnings };
}

/* ------------------------------------------------------------------ */
/* Modo 2: unidades vendidas por plato                                 */
/* ------------------------------------------------------------------ */

export interface UnitsImportRow {
  /** Nombre tal y como venia en el archivo (o el codigo, si no traia nombre). */
  importedName: string;
  /** Codigo tal y como venia en el archivo, si habia columna de codigo. */
  importedCode?: string;
  units: number;
}

export interface UnitsMatch extends UnitsImportRow {
  menuItemId: string;
  /** Nombre del plato en la carta. */
  menuItemName: string;
  /** true si el nombre no era identico y se ha emparejado por parecido. */
  fuzzy: boolean;
  /** true si se ha emparejado por el codigo del plato (lo mas fiable). */
  porCodigo?: boolean;
}

export interface UnitsMenuItem {
  menuItemId: string;
  name: string;
  /** Codigo de la ficha de la receta, si lo tiene. */
  code?: string;
}

export interface UnitsImportResult {
  matched: UnitsMatch[];
  unmatched: UnitsImportRow[];
  skippedRows: number;
}

/**
 * Filas mapeadas → unidades por plato, agregadas por codigo o por nombre.
 *
 * El codigo manda (mejora 64 de Alex): si el archivo lo trae y coincide con
 * el de la ficha de la receta, ese es el plato aunque el nombre difiera. Sin
 * codigo, o sin coincidencia, se empareja por nombre exacto y, en ultimo
 * caso, por parecido si el candidato es unico.
 */
export function buildUnitsImport(
  rows: string[][],
  targets: ImportTarget[],
  menuItems: UnitsMenuItem[],
): UnitsImportResult {
  const dishIndex = targets.findIndex((t) => t.kind === 'dish');
  const codeIndex = targets.findIndex((t) => t.kind === 'code');
  const unitsIndex = targets.findIndex((t) => t.kind === 'units');
  if ((dishIndex === -1 && codeIndex === -1) || unitsIndex === -1) {
    return { matched: [], unmatched: [], skippedRows: rows.length };
  }

  // Agrega por codigo (o nombre) normalizado: un TPV que exporta una fila
  // por dia repite el plato muchas veces.
  const totals = new Map<string, UnitsImportRow>();
  let skippedRows = 0;
  for (const row of rows) {
    const name = dishIndex === -1 ? '' : (row[dishIndex] ?? '').trim();
    const code = codeIndex === -1 ? '' : (row[codeIndex] ?? '').trim();
    const units = parseImportNumber(row[unitsIndex]);
    if ((name === '' && code === '') || units === undefined) {
      skippedRows++;
      continue;
    }
    const key = code !== '' ? `c:${normalizeText(code)}` : `n:${normalizeText(name)}`;
    if (key === 'c:' || key === 'n:') {
      skippedRows++;
      continue;
    }
    const current = totals.get(key);
    if (current) {
      current.units += units;
    } else {
      totals.set(key, {
        importedName: name || code,
        ...(code !== '' ? { importedCode: code } : {}),
        units,
      });
    }
  }

  const byCode = new Map<string, UnitsMenuItem>();
  const byExact = new Map<string, UnitsMenuItem>();
  for (const item of menuItems) {
    const code = normalizeText(item.code ?? '');
    if (code !== '' && !byCode.has(code)) byCode.set(code, item);
    byExact.set(normalizeText(item.name), item);
  }

  const matched: UnitsMatch[] = [];
  const unmatched: UnitsImportRow[] = [];
  for (const row of totals.values()) {
    const units = Math.max(0, Math.round(row.units));
    const porCodigo = row.importedCode ? byCode.get(normalizeText(row.importedCode)) : undefined;
    if (porCodigo) {
      matched.push({
        ...row,
        units,
        menuItemId: porCodigo.menuItemId,
        menuItemName: porCodigo.name,
        fuzzy: false,
        porCodigo: true,
      });
      continue;
    }
    const key = normalizeText(row.importedName);
    const exact = key === '' ? undefined : byExact.get(key);
    if (exact) {
      matched.push({
        ...row,
        units,
        menuItemId: exact.menuItemId,
        menuItemName: exact.name,
        fuzzy: false,
      });
      continue;
    }
    // Parecido: un nombre contiene al otro ("Paella" ↔ "Paella de marisco").
    // Solo si el candidato es UNICO; con dudas, mejor preguntar que acertar.
    const candidates =
      key === ''
        ? []
        : menuItems.filter((item) => {
            const itemKey = normalizeText(item.name);
            return itemKey.includes(key) || key.includes(itemKey);
          });
    if (candidates.length === 1) {
      matched.push({
        ...row,
        units,
        menuItemId: candidates[0].menuItemId,
        menuItemName: candidates[0].name,
        fuzzy: true,
      });
    } else {
      unmatched.push({ ...row, units });
    }
  }

  matched.sort((a, b) => b.units - a.units);
  unmatched.sort((a, b) => b.units - a.units);
  return { matched, unmatched, skippedRows };
}

/* ------------------------------------------------------------------ */
/* Ayuda para adaptar el fichero de otro TPV                           */
/* ------------------------------------------------------------------ */

/**
 * Formato estandar de ventas de Alexia. Es el que reconoce el importador sin
 * tener que tocar nada, y el que se le pide a un TPV cualquiera.
 */
export const VENTAS_TEMPLATE_HEADERS = [
  'Fecha',
  'Efectivo',
  'Tarjeta',
  'Otros métodos',
  'Comensales',
  'Mesas',
  'Cortesías',
  'Descuentos',
];

export const VENTAS_TEMPLATE_ROWS: string[][] = [
  ['01/06/2026', '420,50', '1.180,30', '0', '96', '34', '0', '35,00'],
  ['02/06/2026', '385,00', '1.024,75', '55,20', '84', '29', '18,50', '0'],
];

/**
 * Prompt para convertir el export de un TPV a nuestra plantilla.
 *
 * Es la version sencilla de la mejora 10 de Alex: en vez de perseguir la
 * compatibilidad con todos los TPV del mercado, se le da al usuario nuestro
 * formato y un texto que hace la conversion en cualquier asistente.
 *
 * Probandolo con exports reales, Alex vio que el modelo se equivoca al asignar
 * columnas y que el archivo resultante puede PARECER correcto (2 sep 2026).
 * De ahi dos cosas: la plantilla se nombra como formato de destino obligatorio
 * —antes solo se describian las columnas, y el modelo se inventaba las suyas—
 * y se le exige que declare el emparejamiento, para que el usuario pueda
 * comprobarlo. La pantalla, ademas, ya no vende esto como solucion cerrada.
 */
export function promptAdaptarVentas(plataformas: string[] = []): string {
  const columnas = [...VENTAS_TEMPLATE_HEADERS, ...plataformas].join(' | ');
  return [
    'Vas a recibir dos archivos:',
    '',
    '1. «plantilla-ventas-alexia-rest», que contiene el formato EXACTO al que hay que llegar.',
    '2. El archivo exportado de mi TPV, con mis datos reales.',
    '',
    'Convierte los datos de mi TPV al formato exacto de la plantilla. Respeta los nombres de',
    'las columnas, su estructura y su orden. No añadas, elimines ni renombres columnas.',
    '',
    'Las columnas de la plantilla son, en este orden:',
    '',
    columnas,
    '',
    'Reglas:',
    '1. No copies los datos de ejemplo de la plantilla: usa solo su estructura y rellénala con',
    '   los datos de mi archivo.',
    '2. Una fila por día. Si el archivo trae varias filas del mismo día (turnos, tickets),',
    '   súmalas.',
    '3. La fecha en formato DD/MM/AAAA.',
    '4. Los importes SIN IVA. Si el archivo los da con IVA, réstalo y dime qué tipo has',
    '   aplicado. Si no puedes saber si llevan IVA, pregúntamelo antes de convertir nada.',
    '5. No inventes datos. Una columna que mi archivo no tenga se deja vacía.',
    '6. Al final, dime columna por columna cuál de mi archivo has puesto en cada una de la',
    '   plantilla, y qué has descartado. Si de alguna no estás seguro, dilo en vez de decidir.',
    '',
    'Devuélvemelo como CSV para descargar.',
  ].join('\n');
}
